<template>
  <v-container fluid max-width="1200px">
    <v-alert v-if="!authorized" type="error" class="mt-4">
      You do not have permission to access the Payroll Manager view.
    </v-alert>

    <PayrollManagerView
      v-else
      :user="user"
      :users="usersList"
      :companies="companiesList"
      :exceptions="exceptions"
      :isLoadingExceptions="isLoadingExceptions"
      :payrollRate="payrollRate"
      :isLoadingRate="isLoadingRate"
      :ptOnlyMode="ptOnlyMode"
      :submissionsRefreshKey="submissionsRefreshKey"
      :ptSubmissionsRefreshKey="ptSubmissionsRefreshKey"
      :lastUpdatedSubmission="lastUpdatedSubmission"
      :lastUpdatedPTSubmission="lastUpdatedPTSubmission"
      @approve="handleApproveSubmission"
      @deny="handleDenySubmission"
      @uploadReceipt="handleUploadReceipt"
      @updatePaymentDate="handleUpdatePaymentDate"
      @updateTransferFee="handleUpdateTransferFee"
      @updateDollarRate="handleUpdateDollarRate"
      @update-currency="handleUpdateCurrency"
      @updateAdminComments="handleUpdateAdminComments"
      @updateRate="handleUpdateRate"
      @savePayrollRate="handleSavePayrollRate"
      @delete-submission="handleDeleteSubmission"
      @restore-submission="handleRestoreSubmission"
      @unapprove-submission="handleUnapproveSubmission"
      @pt-approve="handlePTApproveSubmission"
      @pt-deny="handlePTDenySubmission"
      @pt-update-payment-date="handlePTUpdatePaymentDate"
      @pt-update-transfer-fee="handlePTUpdateTransferFee"
      @pt-update-dollar-rate="handlePTUpdateDollarRate"
      @pt-update-currency="handlePTUpdateCurrency"
      @pt-update-admin-comments="handlePTUpdateAdminComments"
      @pt-update-status-field="handlePTUpdateStatusField"
      @resolve-exception="handleResolveException"
      @unresolve-exception="handleUnresolveException"
      @update-exception-notes="handleUpdateExceptionNotes"
      @pt-delete-submission="handlePTDeleteSubmission"
      @pt-restore-submission="handlePTRestoreSubmission"
      @pt-unapprove-submission="handlePTUnapproveSubmission"
      @pt-upload-additional-document="handlePTUploadAdditionalDocument"
      @pt-delete-additional-document="handlePTDeleteAdditionalDocument"
      @upload-additional-document="handleUploadAdditionalDocument"
      @delete-additional-document="handleDeleteAdditionalDocument"
      @upload-invoice="handleUploadInvoice"
      @pt-upload-invoice="handlePTUploadInvoice"
      @refresh="loadManagerData"
    />
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import PayrollManagerView from "./components/PayrollManagerView.vue";

export default {
  name: "PayrollManagerPage",
  inject: ["users", "companies"],
  components: {
    PayrollManagerView,
  },
  data() {
    return {
      authorized: false,
      exceptions: [],
      isLoadingExceptions: false,
      payrollRate: { dollar_rate: 0, transfer_fee: 0 },
      isLoadingRate: false,
      // Incrementing these triggers a re-fetch in PayrollManagerView
      submissionsRefreshKey: 0,
      ptSubmissionsRefreshKey: 0,
      lastUpdatedSubmission: null,
      lastUpdatedPTSubmission: null,
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/get_user",
    }),
    usersList() {
      return this.users ? this.users() : [];
    },
    companiesList() {
      return this.companies ? this.companies() : [];
    },
    isPayrollManager() {
      const payrollManagerEmails = ['dillongunn@gmail.com', 'daniza@stagit.ie', 'gammelldean@gmail.com', 'accounts.nalie@stagit.ie', 'accounts.arjaylon@stagit.ie'];
      return payrollManagerEmails.includes(this.user?.email?.toLowerCase());
    },
    isPTPayrollManager() {
      const ptPayrollManagerEmails = ['accounts@profitabletradesmen.com'];
      return ptPayrollManagerEmails.includes(this.user?.email?.toLowerCase());
    },
    ptOnlyMode() {
      return this.isPTPayrollManager;
    },
  },
  async mounted() {
    if (!this.isPayrollManager && !this.isPTPayrollManager) {
      this.authorized = false;
      this.$toast.error('You do not have permission to access this page');
      return;
    }
    this.authorized = true;
    await this.loadManagerData();
  },
  methods: {
    async loadManagerData() {
      try {
        this.isLoadingExceptions = true;
        const exceptionsResponse = await this.$axios.get('/api/payroll/manager/exceptions', { params: { limit: 500 } });
        if (exceptionsResponse.data.response || exceptionsResponse.data.data) {
          this.exceptions = exceptionsResponse.data.data || [];
        }
        await this.loadPayrollRate();
      } catch (error) {
        console.error('Error loading manager data:', error);
        this.$toast.error('Failed to load payroll data');
      } finally {
        this.isLoadingExceptions = false;
      }
    },

    async loadPayrollRate() {
      try {
        this.isLoadingRate = true;
        const response = await this.$axios.get('/api/payroll/manager/payroll-rate');
        if (response.data.response && response.data.data) {
          this.payrollRate = response.data.data;
        }
      } catch (error) {
        console.error('Error loading payroll rate:', error);
      } finally {
        this.isLoadingRate = false;
      }
    },

    async handleApproveSubmission(submission, approvedAmount) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/submissions/${submission.id}/approve`,
          { approved_amount: approvedAmount }
        );

        if (response.data.response) {
          this.$toast.success(response.data.message || `Submission ${submission.invoice_number} approved`);
          this.submissionsRefreshKey++;
        } else {
          this.$toast.error(response.data.message || 'Failed to approve submission');
        }
      } catch (error) {
        console.error('Error approving submission:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to approve submission');
      }
    },

    async handleDenySubmission(submission, reason) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/submissions/${submission.id}/deny`,
          { deny_reason: reason }
        );

        if (response.data.response) {
          this.$toast.warning(response.data.message || `Submission ${submission.invoice_number} denied`);
          this.submissionsRefreshKey++;
        } else {
          this.$toast.error(response.data.message || 'Failed to deny submission');
        }
      } catch (error) {
        console.error('Error denying submission:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to deny submission');
      }
    },

    async handleDeleteSubmission(submission) {
      try {
        const response = await this.$axios.delete(
          `/api/payroll/manager/submissions/${submission.id}`
        );

        if (response.data.response) {
          this.$toast.success(response.data.message || `Submission ${submission.invoice_number} deleted`);
          this.submissionsRefreshKey++;
        } else {
          this.$toast.error(response.data.message || 'Failed to delete submission');
        }
      } catch (error) {
        console.error('Error deleting submission:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to delete submission');
      }
    },

    async handleRestoreSubmission(submission) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/submissions/${submission.id}/restore`
        );

        if (response.data.response) {
          this.$toast.success(response.data.message || `Submission ${submission.invoice_number} restored`);
          this.submissionsRefreshKey++;
        } else {
          this.$toast.error(response.data.message || 'Failed to restore submission');
        }
      } catch (error) {
        console.error('Error restoring submission:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to restore submission');
      }
    },

    async handleUnapproveSubmission(submission) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/submissions/${submission.id}/unapprove`
        );

        if (response.data.response) {
          this.$toast.success(response.data.message || `Submission ${submission.invoice_number} unapproved`);
          this.submissionsRefreshKey++;
        } else {
          this.$toast.error(response.data.message || 'Failed to unapprove submission');
        }
      } catch (error) {
        console.error('Error unapproving submission:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to unapprove submission');
      }
    },

    async handleUploadReceipt(submission, file) {
      try {
        const formData = new FormData();
        formData.append('receipt_file', file);

        const response = await this.$axios.post(
          `/api/payroll/manager/submissions/${submission.id}/receipt`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.response) {
          this.$toast.success(response.data.message || 'Payment receipt uploaded');
          this.submissionsRefreshKey++;
        } else {
          this.$toast.error(response.data.message || 'Failed to upload receipt');
        }
      } catch (error) {
        console.error('Error uploading receipt:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to upload receipt');
      }
    },

    async handleUpdatePaymentDate(submission, date) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/submissions/${submission.id}/payment-date`,
          { payment_date: date }
        );

        if (response.data.response) {
          this.$toast.success(response.data.message || 'Payment date updated');
          this.submissionsRefreshKey++;
          this.lastUpdatedSubmission = { ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to update payment date');
        }
      } catch (error) {
        console.error('Error updating payment date:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to update payment date');
      }
    },

    async handleUpdateTransferFee(submissionId, transferFee) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/submissions/${submissionId}/transfer-fee`,
          { transfer_fee: transferFee }
        );

        if (response.data.response) {
          this.$toast.success('Transfer fee updated');
          this.lastUpdatedSubmission = { ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to update transfer fee');
        }
      } catch (error) {
        console.error('Error updating transfer fee:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to update transfer fee');
      }
    },

    async handleUpdateDollarRate(submissionId, dollarRate) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/submissions/${submissionId}/dollar-rate`,
          { dollar_rate: dollarRate }
        );

        if (response.data.response) {
          this.$toast.success('Dollar rate updated');
          this.lastUpdatedSubmission = { ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to update dollar rate');
        }
      } catch (error) {
        console.error('Error updating dollar rate:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to update dollar rate');
      }
    },

    async handleUpdateCurrency(submission, currency, invoiceAmount) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/submissions/${submission.id}/currency`,
          { currency, invoice_amount: invoiceAmount }
        );

        if (response.data.response) {
          this.$toast.success('Invoice details updated');
          this.lastUpdatedSubmission = { ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to update invoice details');
        }
      } catch (error) {
        console.error('Error updating invoice details:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to update invoice details');
      }
    },

    async handleUpdateAdminComments(submissionId, adminComments) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/submissions/${submissionId}/admin-comments`,
          { admin_comments: adminComments }
        );

        if (response.data.response) {
          this.lastUpdatedSubmission = { ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to update admin comments');
        }
      } catch (error) {
        console.error('Error updating admin comments:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to update admin comments');
      }
    },

    async handleUpdateRate(user, rate, currency) {
      try {
        const response = await this.$axios.put(
          `/api/payroll/manager/va-profiles/${user.id}/rate`,
          {
            hourly_rate: rate,
            payment_currency: currency
          }
        );

        if (response.data.response) {
          this.$toast.success(response.data.message || `Rate updated for ${user.name}`);
        } else {
          this.$toast.error(response.data.message || 'Failed to update rate');
        }
      } catch (error) {
        console.error('Error updating rate:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to update rate');
      }
    },

    async handleSavePayrollRate(dollarRate, transferFee) {
      try {
        const response = await this.$axios.post('/api/payroll/manager/payroll-rate', {
          dollar_rate: dollarRate,
          transfer_fee: transferFee,
        });

        if (response.data.response) {
          this.payrollRate = response.data.data;
          this.$toast.success(response.data.message || 'Payroll rate saved successfully');
        } else {
          this.$toast.error(response.data.message || 'Failed to save payroll rate');
        }
      } catch (error) {
        console.error('Error saving payroll rate:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to save payroll rate');
      }
    },

    /*
    |--------------------------------------------------------------------------
    | PT Payroll Handlers
    |--------------------------------------------------------------------------
    */

    async handlePTApproveSubmission(submission, approvedAmount) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/pt/submissions/${submission.id}/approve`,
          { approved_amount: approvedAmount }
        );

        if (response.data.response) {
          this.$toast.success(response.data.message || `PT Submission ${submission.invoice_number} approved`);
          this.ptSubmissionsRefreshKey++;
        } else {
          this.$toast.error(response.data.message || 'Failed to approve PT submission');
        }
      } catch (error) {
        console.error('Error approving PT submission:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to approve PT submission');
      }
    },

    async handlePTDenySubmission(submission, denyReason) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/pt/submissions/${submission.id}/deny`,
          { deny_reason: denyReason }
        );

        if (response.data.response) {
          this.$toast.success(response.data.message || `PT Submission ${submission.invoice_number} denied`);
          this.ptSubmissionsRefreshKey++;
        } else {
          this.$toast.error(response.data.message || 'Failed to deny PT submission');
        }
      } catch (error) {
        console.error('Error denying PT submission:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to deny PT submission');
      }
    },

    async handlePTUpdatePaymentDate(submission, date) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/pt/submissions/${submission.id}/payment-date`,
          { payment_date: date }
        );

        if (response.data.response) {
          this.$toast.success('PT Payment date updated');
          this.ptSubmissionsRefreshKey++;
          this.lastUpdatedPTSubmission = { ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to update PT payment date');
        }
      } catch (error) {
        console.error('Error updating PT payment date:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to update PT payment date');
      }
    },

    async handlePTUpdateTransferFee(submissionId, transferFee) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/pt/submissions/${submissionId}/transfer-fee`,
          { transfer_fee: transferFee }
        );

        if (response.data.response) {
          this.$toast.success('PT Transfer fee updated');
          this.lastUpdatedPTSubmission = { ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to update PT transfer fee');
        }
      } catch (error) {
        console.error('Error updating PT transfer fee:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to update PT transfer fee');
      }
    },

    async handlePTUpdateDollarRate(submissionId, dollarRate) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/pt/submissions/${submissionId}/dollar-rate`,
          { dollar_rate: dollarRate }
        );

        if (response.data.response) {
          this.$toast.success('PT Dollar rate updated');
          this.lastUpdatedPTSubmission = { ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to update PT dollar rate');
        }
      } catch (error) {
        console.error('Error updating PT dollar rate:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to update PT dollar rate');
      }
    },

    async handlePTUpdateCurrency(submission, currency, invoiceAmount) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/pt/submissions/${submission.id}/currency`,
          { currency, invoice_amount: invoiceAmount }
        );

        if (response.data.response) {
          this.$toast.success('PT Invoice details updated');
          this.lastUpdatedPTSubmission = { ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to update PT invoice details');
        }
      } catch (error) {
        console.error('Error updating PT invoice details:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to update PT invoice details');
      }
    },

    async handlePTUpdateAdminComments(submissionId, adminComments) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/pt/submissions/${submissionId}/admin-comments`,
          { admin_comments: adminComments }
        );

        if (response.data.response) {
          this.lastUpdatedPTSubmission = { ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to update PT admin comments');
        }
      } catch (error) {
        console.error('Error updating PT admin comments:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to update PT admin comments');
      }
    },

    async handlePTUpdateStatusField(submissionId, field, value) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/pt/submissions/${submissionId}/status-field`,
          { field, value }
        );

        if (response.data.response) {
          this.lastUpdatedPTSubmission = { ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to update field');
        }
      } catch (error) {
        console.error('Error updating PT status field:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to update field');
      }
    },

    async handleResolveException(exceptionId, resolutionNotes) {
      try {
        const index = this.exceptions.findIndex(e => e.id === exceptionId);
        if (index !== -1) {
          this.$set(this.exceptions[index], 'is_resolved', true);
        }

        const response = await this.$axios.post(
          `/api/payroll/manager/exceptions/${exceptionId}/resolve`,
          { resolution_notes: resolutionNotes }
        );

        if (response.data.response) {
          if (index !== -1 && response.data.data) {
            this.$set(this.exceptions, index, response.data.data);
          }
          this.$toast.success('Exception resolved');
        } else {
          if (index !== -1) this.$set(this.exceptions[index], 'is_resolved', false);
          this.$toast.error(response.data.message || 'Failed to resolve exception');
        }
      } catch (error) {
        console.error('Error resolving exception:', error);
        const index = this.exceptions.findIndex(e => e.id === exceptionId);
        if (index !== -1) this.$set(this.exceptions[index], 'is_resolved', false);
        this.$toast.error(error.response?.data?.message || 'Failed to resolve exception');
      }
    },

    async handleUnresolveException(exceptionId) {
      try {
        const index = this.exceptions.findIndex(e => e.id === exceptionId);
        if (index !== -1) {
          this.$set(this.exceptions[index], 'is_resolved', false);
        }

        const response = await this.$axios.post(
          `/api/payroll/manager/exceptions/${exceptionId}/unresolve`
        );

        if (response.data.response) {
          if (index !== -1 && response.data.data) {
            this.$set(this.exceptions, index, response.data.data);
          }
          this.$toast.success('Exception unresolved');
        } else {
          if (index !== -1) this.$set(this.exceptions[index], 'is_resolved', true);
          this.$toast.error(response.data.message || 'Failed to unresolve exception');
        }
      } catch (error) {
        console.error('Error unresolving exception:', error);
        const index = this.exceptions.findIndex(e => e.id === exceptionId);
        if (index !== -1) this.$set(this.exceptions[index], 'is_resolved', true);
        this.$toast.error(error.response?.data?.message || 'Failed to unresolve exception');
      }
    },

    async handleUpdateExceptionNotes(exceptionId, notes) {
      try {
        const index = this.exceptions.findIndex(e => e.id === exceptionId);
        if (index !== -1) {
          this.$set(this.exceptions[index], 'resolution_notes', notes);
        }

        const response = await this.$axios.post(
          `/api/payroll/manager/exceptions/${exceptionId}/notes`,
          { resolution_notes: notes }
        );

        if (!response.data.response) {
          this.$toast.error(response.data.message || 'Failed to update notes');
        }
      } catch (error) {
        console.error('Error updating exception notes:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to update notes');
      }
    },

    async handlePTDeleteSubmission(submission) {
      try {
        const response = await this.$axios.delete(
          `/api/payroll/manager/pt/submissions/${submission.id}`
        );

        if (response.data.response) {
          this.$toast.success(response.data.message || `PT Submission ${submission.invoice_number} deleted`);
          this.ptSubmissionsRefreshKey++;
        } else {
          this.$toast.error(response.data.message || 'Failed to delete PT submission');
        }
      } catch (error) {
        console.error('Error deleting PT submission:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to delete PT submission');
      }
    },

    async handlePTRestoreSubmission(submission) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/pt/submissions/${submission.id}/restore`
        );

        if (response.data.response) {
          this.$toast.success(response.data.message || `PT Submission ${submission.invoice_number} restored`);
          this.ptSubmissionsRefreshKey++;
        } else {
          this.$toast.error(response.data.message || 'Failed to restore PT submission');
        }
      } catch (error) {
        console.error('Error restoring PT submission:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to restore PT submission');
      }
    },

    async handlePTUnapproveSubmission(submission) {
      try {
        const response = await this.$axios.post(
          `/api/payroll/manager/pt/submissions/${submission.id}/unapprove`
        );

        if (response.data.response) {
          this.$toast.success(response.data.message || `PT Submission ${submission.invoice_number} unapproved`);
          this.ptSubmissionsRefreshKey++;
        } else {
          this.$toast.error(response.data.message || 'Failed to unapprove PT submission');
        }
      } catch (error) {
        console.error('Error unapproving PT submission:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to unapprove PT submission');
      }
    },

    async handlePTUploadAdditionalDocument(submission, file) {
      try {
        const formData = new FormData();
        formData.append('additional_document', file);

        const response = await this.$axios.post(
          `/api/payroll/manager/pt/submissions/${submission.id}/additional-document`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.response) {
          this.$toast.success('Additional document uploaded');
          this.lastUpdatedPTSubmission = { ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to upload additional document');
        }
      } catch (error) {
        console.error('Error uploading additional document:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to upload additional document');
      }
    },

    async handlePTDeleteAdditionalDocument(submission) {
      try {
        const response = await this.$axios.delete(
          `/api/payroll/manager/pt/submissions/${submission.id}/additional-document`
        );

        if (response.data.response) {
          this.$toast.success('Additional document removed');
          this.lastUpdatedPTSubmission = { ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to remove additional document');
        }
      } catch (error) {
        console.error('Error removing additional document:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to remove additional document');
      }
    },

    async handleUploadAdditionalDocument(submission, file) {
      try {
        const formData = new FormData();
        formData.append('additional_document', file);

        const response = await this.$axios.post(
          `/api/payroll/manager/submissions/${submission.id}/additional-document`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.response) {
          this.$toast.success('Additional document uploaded');
          this.lastUpdatedSubmission = { ...submission, ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to upload additional document');
        }
      } catch (error) {
        console.error('Error uploading additional document:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to upload additional document');
      }
    },

    async handleDeleteAdditionalDocument(submission) {
      try {
        const response = await this.$axios.delete(
          `/api/payroll/manager/submissions/${submission.id}/additional-document`
        );

        if (response.data.response) {
          this.$toast.success('Additional document removed');
          this.lastUpdatedSubmission = { ...submission, ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to remove additional document');
        }
      } catch (error) {
        console.error('Error removing additional document:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to remove additional document');
      }
    },

    async uploadInvoiceFile(url, file) {
      const formData = new FormData();
      formData.append('invoice_file', file);
      return this.$axios.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    },

    async handleUploadInvoice(submission, file) {
      try {
        const response = await this.uploadInvoiceFile(`/api/payroll/manager/submissions/${submission.id}/invoice`, file);

        if (response.data.response) {
          this.$toast.success('Invoice replaced');
          this.lastUpdatedSubmission = { ...submission, ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to upload invoice');
        }
      } catch (error) {
        console.error('Error uploading invoice:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to upload invoice');
      }
    },

    async handlePTUploadInvoice(submission, file) {
      try {
        const response = await this.uploadInvoiceFile(`/api/payroll/manager/pt/submissions/${submission.id}/invoice`, file);

        if (response.data.response) {
          this.$toast.success('Invoice replaced');
          this.lastUpdatedPTSubmission = { ...submission, ...response.data.data, _ts: Date.now() };
        } else {
          this.$toast.error(response.data.message || 'Failed to upload invoice');
        }
      } catch (error) {
        console.error('Error uploading invoice:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to upload invoice');
      }
    },
  },
};
</script>

<style scoped>
</style>
